import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import { AppEnvironment } from '@internal/open-catalog';
import { Card, CardContent, Grid, IconButton, Typography } from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import CachedIcon from '@material-ui/icons/Cached';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import HourglassEmptyOutlinedIcon from '@material-ui/icons/HourglassEmptyOutlined';
import React, { useCallback, useMemo, useReducer, useState } from 'react';
import { FetchMqStatsService } from '../../../../../../services/FetchMqStatsService';
import useAsync from 'react-use/esm/useAsync';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    noPadding: {
      padding: 0
    },
    clickable: {
      cursor: 'pointer'
    },
    card: {
      padding: 16,
      minWidth: 300,
      paddingBottom: 8,
      borderBottom: '4px solid white',
      '&:hover': {
        filter: 'brightness(110%)'
      }
    },
    bigNumberContainer: {
      textAlign: 'right',
    },
    bigNumber: {
      fontSize: '3rem',
      fontWeight: 'bold',
      transition: 'color 0.5s ease-in-out',
    },
    greenText: {
      color: '#4caf50', // green for 0
    },
    redText: {
      color: '#f44336', // red for >0
    },
    loadingText: {
      color: '#9e9e9e', // grey for loading,
    },
    selected: {
      '& > div': {
        borderColor: theme.page.colors[1]
      }
    }
  })
)

export interface CardData {
  title: string;
  subtitle: string;
  queues: string[];
  env: AppEnvironment;
  vhost: string;
  value?: number;
  color?: string;
}

interface ParkingLotCardProps {
  dev: CardData;
  stg: CardData;
  prd: CardData;
  onCardSelectionChanged: (selected: CardData) => void
}

export const ParkingLotCards: React.FC<ParkingLotCardProps> = ({ dev, stg, prd, onCardSelectionChanged }) => {

  const classes = useStyles();

  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [selected, setSelected] = useState<CardData>(prd);

  const data: CardData[] = useMemo(() => [dev, stg, prd], [dev, stg, prd]);

  const [items, setItems] = useReducer((state: CardData[], action: { payload: CardData, index: number }): CardData[] => {
    state.splice(action.index, 1, action.payload);
    return [...state];
  }, []);

  const { value: proxyUrl } = useAsync(() => {
    return discoveryApi.getBaseUrl('proxy');
  }, [discoveryApi]);


  const fetchQueueCount = useCallback((item: CardData, index: number) => {
    if (proxyUrl) {
      const mqAccess = new FetchMqStatsService(proxyUrl, fetchApi, item.env, item.vhost);
      item.value = 0;
      item.color = classes.loadingText;
      setItems({ payload: item, index });
      Promise.all(item.queues.map(queue => mqAccess.messagesCount(queue)))
        .then((result) => result.reduce((acc, cur) => acc + cur, 0))
        .then((sum) => setItems({ payload: { ...item, value: sum, color: sum > 0 ? classes.redText : classes.greenText }, index }));
    }
  }, [fetchApi, setItems, classes, proxyUrl]);

  const selectCard = (card: CardData) => {
    setSelected(card);
    onCardSelectionChanged(card);
  };

  useMemo(() => {
    data.forEach((item, index) => fetchQueueCount(item, index));
    onCardSelectionChanged(selected);
  }, [data, fetchQueueCount, onCardSelectionChanged, selected]);

  const handleRefresh = (item:CardData, index: number) => {
    fetchQueueCount(item, index);
    // Triggers ParkingLotMessageReprocessor to fetch new messages
    onCardSelectionChanged({ ...item, queues: [...item.queues] }); 
  };

  return (
    <>
      <Grid container>
        {items.map((item, index) => (
          <Grid item key={index} xs={4}>
            <Grid item key={index} className={item.env === selected?.env ? classes.selected : ''}>
              <Card className={classes.card}>
                <CardContent className={classes.noPadding} >
                  <Grid container alignItems="center">
                    <Grid item xs={6} className={classes.clickable} aria-label="select" onClick={() => selectCard(item)}>
                      <Typography variant="h6">{item.title}</Typography>
                      <Typography variant="subtitle1">{item.subtitle}</Typography>
                      {item.color === classes.loadingText && <HourglassEmptyOutlinedIcon className={classes.loadingText} />}
                      {item.color === classes.greenText && <CheckCircleOutlineIcon className={classes.greenText} />}
                      {item.color === classes.redText && <ErrorOutlineIcon className={classes.redText} />}
                    </Grid>
                    <Grid className={classes.bigNumberContainer} item xs={6}>
                      <IconButton className={classes.noPadding} aria-label="refresh" onClick={() => handleRefresh(item, index)}>
                        <CachedIcon />
                      </IconButton>
                      <Typography className={`${classes.bigNumber} ${item.color} ${classes.clickable}`} aria-label="select" onClick={() => selectCard(item)}>
                        {item.value}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  );
};